import { SuperSource } from '@slite/search-client'

import askFromAnywhereIllustration from '../assets/ask-from-anywhere.svg'
import oneConnectionIllustration from '../assets/one-connection.svg'
import permissionAwareIllustration from '../assets/permission-aware.svg'

// ─── Source icon row (under the description) ────────────────────────────

export const AI_SEARCH_SOURCES: SuperSource[] = [
  SuperSource.SLITE,
  SuperSource.SLACK_CHANNEL,
  SuperSource.GOOGLE_DRIVE,
  SuperSource.NOTION,
  SuperSource.LINEAR,
  SuperSource.GITHUB,
  SuperSource.HUBSPOT,
  SuperSource.SALESFORCE,
  SuperSource.JIRA,
  SuperSource.CONFLUENCE,
  SuperSource.MICROSOFT_SHAREPOINT,
  SuperSource.ASANA,
  SuperSource.INTERCOM,
  SuperSource.ATTIO,
  SuperSource.GITLAB,
  SuperSource.BIGQUERY,
  // Render once zendesk_mark.svg / zoom_mark.svg land in the slite-cdn logos bucket.
  SuperSource.ZENDESK,
  SuperSource.ZOOM,
]

// ─── Search scenario shape ──────────────────────────────────────────────
//
// One scenario = one question the agent answers, plus the tool calls it
// makes along the way and the markdown answer it streams out. Three of
// these get rotated on the section, mirroring the hero's pattern.

type SearchStep = {
  source: SuperSource
  label: string
}

export type Citation = {
  source: SuperSource
  title: string
  snippet: string
}

type AnswerSource = {
  source: SuperSource
  title: string
  subtitle: string
}

export type AiSearchScenario = {
  id: string
  buttonLabel: string
  question: string
  steps: SearchStep[]
  citations: Record<string, Citation>
  // Markdown — supports `[text](cite:<id>)` to render an inline citation token.
  answer: string
  sources: AnswerSource[]
}

// ─── Scenario 1 — Compliance ────────────────────────────────────────────

const complianceScenario: AiSearchScenario = {
  id: 'compliance',
  buttonLabel: 'Compliance Question',
  question:
    "What's our current SOC 2 and HIPAA compliance status with any exceptions and remediation actions?",
  steps: [
    {
      source: SuperSource.SLITE,
      label: 'Read Security & Compliance Overview in Slite',
    },
    {
      source: SuperSource.SLACK_CHANNEL,
      label: 'Cross-referenced 3 active #security threads in Slack',
    },
    {
      source: SuperSource.GOOGLE_DRIVE,
      label: 'Pulled latest SOC 2 audit from Google Drive',
    },
    {
      source: SuperSource.LINEAR,
      label: 'Verified open remediations in Linear',
    },
  ],
  citations: {
    'soc-2-report': {
      source: SuperSource.GOOGLE_DRIVE,
      title: 'Johanson Group - SOC 2 Type II.pdf',
      snippet: 'Audit period: May 31, 2023 – May 30, 2024',
    },
  },
  answer: `Based on the [latest SOC 2 Type II audit](cite:soc-2-report), Slite's compliance posture has been confirmed across the last full audit cycle.

## SOC 2 Compliance Status

**Current Status:** SOC 2 Type II certified by Johanson Group.

## HIPAA Compliance Status

**Current Status:** Active. BAA in place with all subprocessors.

## Recent Activity

- Bridge letters issued quarterly confirm no material control changes
- One open finding from the Q2 review, remediated and closed
- Next review cycle scheduled for May 2026
`,
  sources: [
    {
      source: SuperSource.SLITE,
      title: 'Security & Compliance Overview',
      subtitle: 'Slite · Compliance / Policies',
    },
    {
      source: SuperSource.GOOGLE_DRIVE,
      title: 'Johanson Group - SOC 2 Type II.pdf',
      subtitle: 'Google Drive · Audit / 2024',
    },
    {
      source: SuperSource.SLACK_CHANNEL,
      title: '#security · 3 threads referenced',
      subtitle: 'Slack',
    },
    {
      source: SuperSource.LINEAR,
      title: 'SEC-142 · SOC 2 remediation',
      subtitle: 'Linear · Security',
    },
  ],
}

// ─── Scenario 2 — Help center / Support ─────────────────────────────────

const supportScenario: AiSearchScenario = {
  id: 'support',
  buttonLabel: 'Support Question',
  question:
    "A customer's team workspace seats aren't syncing after they added 12 new members, what's the right way to resolve it?",
  steps: [
    {
      source: SuperSource.SLITE,
      label: 'Read "Workspace seat sync - troubleshooting"',
    },
    {
      source: SuperSource.INTERCOM,
      label: 'Reviewed 4 similar tickets resolved this month',
    },
    {
      source: SuperSource.LINEAR,
      label: 'Checked open bug reports for seat provisioning',
    },
    {
      source: SuperSource.GITHUB,
      label: 'Confirmed recent billing-service commits',
    },
  ],
  citations: {
    'troubleshoot-doc': {
      source: SuperSource.SLITE,
      title: 'Workspace seat sync - troubleshooting',
      snippet:
        'Seat counts refresh from the billing service every 5 minutes. Manual resync available via Admin → Members.',
    },
    'known-issue': {
      source: SuperSource.LINEAR,
      title: 'BILL-318 · Bulk seat sync delay',
      snippet:
        'Open: bulk imports of 10+ seats may take up to 15 minutes to propagate.',
    },
  },
  answer: `This matches a [known sync delay](cite:known-issue) when 10+ seats are added at once.

## What's happening

Seat counts refresh from the billing service every 5 minutes. Bulk additions of 10+ members hit a known propagation delay tracked in BILL-318.

## How to resolve

1. Ask the admin to open **Admin → Members → Resync seats**
2. If counts still mismatch after 15 minutes, confirm the billing email matches their workspace owner
3. Share the [troubleshooting doc](cite:troubleshoot-doc) so they can self-serve next time

## Escalate when

- Resync returns an error code
- The mismatch persists past 30 minutes
`,
  sources: [
    {
      source: SuperSource.SLITE,
      title: 'Workspace seat sync - troubleshooting',
      subtitle: 'Slite · Help Center / Billing',
    },
    {
      source: SuperSource.INTERCOM,
      title: '4 similar resolved tickets',
      subtitle: 'Intercom · Billing tag',
    },
    {
      source: SuperSource.LINEAR,
      title: 'BILL-318 · Bulk seat sync delay',
      subtitle: 'Linear · Billing',
    },
    {
      source: SuperSource.GITHUB,
      title: 'billing-service commits (last 7d)',
      subtitle: 'GitHub · slite/billing-service',
    },
  ],
}

// ─── Scenario 3 — Sales brief ───────────────────────────────────────────

const salesBriefScenario: AiSearchScenario = {
  id: 'sales-brief',
  buttonLabel: 'Sales Brief',
  question:
    "Brief me on Acme Corp ahead of tomorrow's renewal call: usage, adoption, and any blockers.",
  steps: [
    {
      source: SuperSource.SALESFORCE,
      label: 'Pulled Acme Corp account record from Salesforce',
    },
    {
      source: SuperSource.HUBSPOT,
      label: 'Reviewed last 6 marketing touchpoints in HubSpot',
    },
    {
      source: SuperSource.SLACK_CHANNEL,
      label: 'Cross-referenced #acme-account channel in Slack',
    },
    {
      source: SuperSource.SLITE,
      label: 'Read Acme onboarding playbook in Slite',
    },
  ],
  citations: {
    'sf-account': {
      source: SuperSource.SALESFORCE,
      title: 'Acme Corp · Account',
      snippet: 'Renewal date: 2026-06-30 · ARR: $84k · Owner: Priya Shah',
    },
    'open-blocker': {
      source: SuperSource.SLACK_CHANNEL,
      title: '#acme-account · SSO migration',
      snippet:
        'Customer flagged Okta SCIM provisioning is blocking 40 seats from rolling out.',
    },
  },
  answer: `Renewal call tomorrow, strong adoption, one open blocker on SSO.

## Account snapshot

**[Acme Corp](cite:sf-account)** · ARR $84k · Owner: Priya Shah · Renewal 2026-06-30.

## Adoption signal

- 312 active users (up 28% QoQ)
- AI Search usage in top 10% of accounts on the plan
- Slite Agent rolled out to Engineering and Product

## Open blocker

[SSO migration](cite:open-blocker); Okta SCIM provisioning is holding back 40 seats. CSM has a sync scheduled Thursday.

## Talking points

- Lead with the AI Search adoption win
- Offer a SCIM working session before pricing comes up
- Confirm the multi-year discount is still on the table
`,
  sources: [
    {
      source: SuperSource.SALESFORCE,
      title: 'Acme Corp · Account',
      subtitle: 'Salesforce · Accounts',
    },
    {
      source: SuperSource.HUBSPOT,
      title: '6 touchpoints · last 90 days',
      subtitle: 'HubSpot · Acme Corp',
    },
    {
      source: SuperSource.SLACK_CHANNEL,
      title: '#acme-account · SSO migration',
      subtitle: 'Slack',
    },
    {
      source: SuperSource.SLITE,
      title: 'Acme onboarding playbook',
      subtitle: 'Slite · Sales / Playbooks',
    },
  ],
}

export const AI_SEARCH_SCENARIOS: AiSearchScenario[] = [
  supportScenario,
  salesBriefScenario,
  complianceScenario,
]

// ─── Animation timing knobs (shared with SearchConversation) ────────────

export const STEP_STAGGER_MS = 400 // 1s between step mounts
export const STREAM_TICK_MS = 30
export const STREAM_CHARS_PER_TICK = 6 // ~200 chars/s

// Used by the scenario-button progress fill to know when to advance.
// Steps run in series at STEP_STAGGER_MS each, then the answer streams at
// ~STREAM_CHARS_PER_TICK chars per STREAM_TICK_MS.
export const scenarioPlayDurationMs = (scenario: AiSearchScenario) => {
  const stepsMs = scenario.steps.length * STEP_STAGGER_MS + 2000 // add a small buffer after the last step mounts before streaming starts
  const streamMs =
    Math.ceil(scenario.answer.length / STREAM_CHARS_PER_TICK) * STREAM_TICK_MS
  return stepsMs + streamMs
}

// ─── Feature cards (below the demo) ─────────────────────────────────────

type FeatureCard = {
  title: string
  description: string
  illustration: string
  illustrationAspectRatio: string
}

export const FEATURE_CARDS: FeatureCard[] = [
  {
    title: 'Ask from anywhere',
    description:
      'Slite, Slack, the Chrome extension, or any AI tool over MCP. The same answer wherever your people and agents ask.',
    illustration: askFromAnywhereIllustration,
    illustrationAspectRatio: '387 / 312',
  },
  {
    title: 'Permission-aware',
    description:
      "Yes, even for third-party agents. Every query inherits the user's permissions.",
    illustration: permissionAwareIllustration,
    illustrationAspectRatio: '387 / 301',
  },
  {
    title: 'One connection to your company context',
    description:
      'Connect Slite Agent once instead of wiring an MCP for every tool. Less setup, less cost, one set of permissions.',
    illustration: oneConnectionIllustration,
    illustrationAspectRatio: '387 / 270',
  },
]
